import React from 'react';
import { Users, Video, Eye, RefreshCw, TrendingUp, Clock } from 'lucide-react';
import { StatCard, VisitorSparkline } from './adminShared';

// Admin dashboard "overview" tab (extracted from AdminPage).
export const AdminOverviewTab = ({
  adminApiError,
  channelStats,
  fetchFunnel,
  funnel,
  funnelLoading,
  pendingVideos,
  setActiveTab,
  stats,
  users,
  videos,
  visitorStats,
}) => (
  <div className="space-y-6" data-testid="admin-overview">

    {/* API error banner — shown when admin endpoints fail (typically
        expired token). Previously this was masked by mock fallback
        data showing fake numbers like "125,000 plays". */}
    {adminApiError && (
      <div
        className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-100 text-sm flex items-start gap-3"
        data-testid="admin-api-error-banner"
      >
        <div className="flex-1">
          <p className="font-semibold">
            {adminApiError === 'unauthorized'
              ? "Your admin session expired — the numbers below aren't loading."
              : "Couldn't reach the admin API — the numbers below aren't loading."}
          </p>
          <p className="text-amber-200/80 text-xs mt-1">
            {adminApiError === 'unauthorized'
              ? 'Sign out and sign back in to refresh your session.'
              : 'Check your connection and refresh the page.'}
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs font-medium"
          data-testid="admin-api-error-reload"
        >
          Reload
        </button>
      </div>
    )}

    {/* HERO: Daily Active Visitors. The one number that matters most. */}
    <div className="bg-gradient-to-br from-cyan-500/10 via-zinc-900 to-fuchsia-500/10 rounded-2xl border border-cyan-500/30 p-6" data-testid="hero-daily-visitors">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold mb-1">Daily Active Visitors</p>
          <p className="text-5xl sm:text-6xl font-bold text-white tabular-nums leading-none" data-testid="hero-visitors-today">
            {visitorStats.summary?.unique_today ?? 0}
          </p>
          <p className="text-sm text-zinc-400 mt-2">
            Anonymous IPs that visited today. Existing users + bots excluded.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-right">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Brand-new today</p>
            <p className="text-2xl font-semibold text-fuchsia-300 tabular-nums" data-testid="hero-visitors-new-today">{visitorStats.summary?.brand_new_today ?? 0}</p>
            <p className="text-[10px] text-zinc-500">never seen before</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Last 7 days</p>
            <p className="text-2xl font-semibold text-zinc-100 tabular-nums" data-testid="hero-visitors-7d">{visitorStats.summary?.unique_7d ?? 0}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Last 30 days</p>
            <p className="text-2xl font-semibold text-zinc-100 tabular-nums" data-testid="hero-visitors-30d">{visitorStats.summary?.unique_30d ?? 0}</p>
          </div>
        </div>
      </div>
      {/* Inline 30-day visitor sparkline */}
      {Array.isArray(visitorStats.daily) && visitorStats.daily.length > 0 && (
        <div className="mt-5 pt-4 border-t border-zinc-800">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Daily traffic · last 30 days</p>
          <VisitorSparkline daily={visitorStats.daily} />
        </div>
      )}
    </div>

    {/* 24/7 LIVE CHANNEL — Iter 121. Sits right under the Daily
        Active Visitors hero, same visual language. Shows all 5
        metrics the user asked for. */}
    <div
      className="bg-gradient-to-br from-red-500/10 via-zinc-900 to-orange-500/10 rounded-2xl border border-red-500/25 p-6"
      data-testid="hero-live-channel"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-red-300 font-semibold mb-1 flex items-center gap-2">
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-red-500" />
            </span>
            24/7 Live Channel
          </p>
          <p className="text-5xl sm:text-6xl font-bold text-white tabular-nums leading-none" data-testid="live-qualified-today">
            {channelStats.summary?.qualified_today ?? 0}
          </p>
          <p className="text-sm text-zinc-400 mt-2">
            Unique viewers who stayed on /live for ≥10 seconds today. Bounces + bots excluded.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-right">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Last 7 days</p>
            <p className="text-2xl font-semibold text-zinc-100 tabular-nums" data-testid="live-qualified-7d">{channelStats.summary?.qualified_7d ?? 0}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Last 30 days</p>
            <p className="text-2xl font-semibold text-zinc-100 tabular-nums" data-testid="live-qualified-30d">{channelStats.summary?.qualified_30d ?? 0}</p>
          </div>
        </div>
      </div>

      {/* Session length + banner CTR row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-zinc-800">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Median session</p>
          <p className="text-xl font-semibold text-white tabular-nums" data-testid="live-median-session">
            {(() => { const s = channelStats.summary?.median_session_seconds || 0; const m = Math.floor(s/60); const r = s%60; return `${m}:${String(r).padStart(2,'0')}`; })()}
          </p>
          <p className="text-[10px] text-zinc-500">
            {channelStats.summary?.sessions_measured || 0} session(s)
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">P90 session</p>
          <p className="text-xl font-semibold text-zinc-200 tabular-nums" data-testid="live-p90-session">
            {(() => { const s = channelStats.summary?.p90_session_seconds || 0; const m = Math.floor(s/60); const r = s%60; return `${m}:${String(r).padStart(2,'0')}`; })()}
          </p>
          <p className="text-[10px] text-zinc-500">top 10% dwell</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Banner impressions</p>
          <p className="text-xl font-semibold text-white tabular-nums" data-testid="live-banner-impressions">
            {channelStats.summary?.banner_impressions || 0}
          </p>
          <p className="text-[10px] text-zinc-500">
            {channelStats.summary?.banner_clicks || 0} clicks
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Banner CTR</p>
          <p className="text-xl font-semibold text-fuchsia-300 tabular-nums" data-testid="live-banner-ctr">
            {(channelStats.summary?.banner_ctr_pct ?? 0).toFixed(1)}%
          </p>
          <p className="text-[10px] text-zinc-500">home → /live tap-through</p>
        </div>
      </div>

      {/* Referrer split + top exit videos side by side */}
      {((channelStats.referrer_split?.length > 0) || (channelStats.exit_videos?.length > 0)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-zinc-800 mt-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Where viewers come from</p>
            <div className="space-y-1.5" data-testid="live-referrer-split">
              {(channelStats.referrer_split || []).map((r) => {
                const total = (channelStats.referrer_split || []).reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
                const label = {
                  banner: 'Home LiveBanner',
                  direct: 'Direct link / bookmark',
                  internal: 'Another AUDIVUE page',
                  external: 'External site',
                }[r.source] || r.source;
                return (
                  <div key={r.source} className="flex items-center justify-between gap-3">
                    <div className="text-sm text-zinc-300 truncate flex-1">{label}</div>
                    <div className="text-sm font-semibold text-white tabular-nums w-12 text-right">{r.count}</div>
                    <div className="text-xs text-zinc-500 w-10 text-right">{pct}%</div>
                  </div>
                );
              })}
              {(channelStats.referrer_split || []).length === 0 && (
                <div className="text-xs text-zinc-500">No qualified viewers yet in this window.</div>
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Top drop-off videos</p>
            <div className="space-y-1.5" data-testid="live-exit-videos">
              {(channelStats.exit_videos || []).slice(0, 6).map((v) => (
                <div key={v.video_id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-200 truncate">{v.title || '(unknown)'}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{v.artist || '—'}</div>
                  </div>
                  <div className="text-sm font-semibold text-red-300 tabular-nums flex-shrink-0">
                    {v.exits}
                  </div>
                </div>
              ))}
              {(channelStats.exit_videos || []).length === 0 && (
                <div className="text-xs text-zinc-500">Not enough exits recorded yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* SUPPORTING NUMBERS — 4 tiles, all clickable to drill in */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overview-supporting-tiles">
      <StatCard
        icon={Users}
        label="Total Signups"
        value={stats.totalUsers || users.length}
        color="blue"
        clickable={true}
        onClick={() => setActiveTab('users')}
      />
      <StatCard
        icon={Video}
        label="Total Videos"
        value={videos.filter(v => !v.is_deleted).length}
        color="purple"
        clickable={true}
        onClick={() => setActiveTab('videos')}
      />
      <StatCard
        icon={Eye}
        label="Total Plays"
        value={stats.totalViews?.toLocaleString() || '0'}
        color="green"
        clickable={true}
        onClick={() => setActiveTab('videos')}
      />
      <StatCard
        icon={Clock}
        label="Pending Review"
        value={pendingVideos.length}
        color="yellow"
        clickable={true}
        onClick={() => setActiveTab('moderation')}
      />
    </div>

    {/* GROWTH FUNNEL — signups → publishers → founders */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden" data-testid="growth-funnel-card">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-400" />
          Growth Funnel
        </h3>
        <button
          onClick={fetchFunnel}
          disabled={funnelLoading}
          className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-40"
          title="Refresh"
          data-testid="growth-funnel-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${funnelLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <div className="px-5 py-4">
        {funnelLoading && !funnel && (
          <p className="text-sm text-zinc-500" data-testid="growth-funnel-loading">Loading…</p>
        )}
        {!funnelLoading && !funnel && (
          <p className="text-sm text-zinc-500" data-testid="growth-funnel-unavailable">
            Metrics endpoint not available on this environment. Redeploy to enable.
          </p>
        )}
        {funnel && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 pr-4">Metric</th>
                  <th className="py-2 pr-4 text-right">All-time</th>
                  <th className="py-2 pr-4 text-right">Last 30 days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                <tr>
                  <td className="py-3 pr-4 text-zinc-300">Signups</td>
                  <td className="py-3 pr-4 text-right font-mono text-white" data-testid="funnel-signups-all">{funnel.all_time?.signups ?? 0}</td>
                  <td className="py-3 pr-4 text-right font-mono text-white" data-testid="funnel-signups-30d">{funnel.last_30_days?.signups ?? 0}</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 text-zinc-300">Publishers <span className="text-zinc-500 text-[11px]">(≥1 video)</span></td>
                  <td className="py-3 pr-4 text-right font-mono text-white" data-testid="funnel-publishers-all">{funnel.all_time?.publishers ?? 0}</td>
                  <td className="py-3 pr-4 text-right font-mono text-white" data-testid="funnel-publishers-30d">{funnel.last_30_days?.publishers ?? 0}</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 text-zinc-300">Founding badges</td>
                  <td className="py-3 pr-4 text-right font-mono text-white" data-testid="funnel-founders-all">{funnel.all_time?.founders_assigned ?? 0}</td>
                  <td className="py-3 pr-4 text-right font-mono text-white" data-testid="funnel-founders-30d">{funnel.last_30_days?.founders_assigned ?? 0}</td>
                </tr>
              </tbody>
            </table>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="rounded-lg bg-zinc-800/50 border border-zinc-800 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Signup → Publish</p>
                <p className="text-base font-semibold text-cyan-300" data-testid="funnel-conv-publish">
                  {funnel.conversion?.all_time_signup_to_publisher_pct ?? 0}%
                </p>
              </div>
              <div className="rounded-lg bg-zinc-800/50 border border-zinc-800 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Publisher → Founder</p>
                <p className="text-base font-semibold text-fuchsia-300" data-testid="funnel-conv-founder">
                  {funnel.conversion?.all_time_publisher_to_founder_pct ?? 0}%
                </p>
              </div>
              <div className="rounded-lg bg-zinc-800/50 border border-zinc-800 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Founding seats left</p>
                <p className="text-base font-semibold text-amber-300" data-testid="funnel-seats">
                  {funnel.founding_seats_remaining ?? 0} / {funnel.founding_cap ?? 50}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

  </div>
);
